<script setup lang="ts">
import type { NavigationTreeFragment } from '#graphql-operations'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { LazySubmenuColumns, LazySubmenuSimple } from '#components'

interface HeaderNavSubmenuProps {
  nav: NavigationTreeFragment
}

defineProps<HeaderNavSubmenuProps>()

const route = useRoute()
const submenuId = useId()

const targetRef = useCurrentElement<HTMLElement>()
const dropdownRef = useTemplateRef<HTMLElement>('dropdownRef')

const open = ref(false)

const isHovered = useMouseHover(targetRef, {
  delayEnter: 100,
})
const { focused } = useFocusWithin(targetRef)
const { activate, deactivate } = useFocusTrap(dropdownRef, {
  initialFocus: false,
  escapeDeactivates: true,
  returnFocusOnDeactivate: true,
  allowOutsideClick: true,
  onDeactivate: () => {
    open.value = false
  },
})

function animateDropdownEnter(el: Element, done: gsap.Callback) {
  gsap.context(() => {
    gsap.timeline({ defaults: { duration: 0.7, ease: 'circ3' }, onComplete: done })
      .set(el, { pointerEvents: 'auto' })
      .set('.dropdown', { clipPath: 'inset(0% 0% 100% 0%)' })
      .set('.dropdown > *', { autoAlpha: 0 })
      .to('.dropdown', { clipPath: 'inset(0% 0% 0% 0%)' })
      .to('.dropdown > *', { autoAlpha: 1, duration: 0.28, ease: 'none' }, 0.1)
  }, el)
}

function animateDropdownLeave(el: Element, done: gsap.Callback) {
  gsap.context(() => {
    gsap.timeline({ defaults: { duration: 0.4, ease: 'circ3' }, onComplete: done })
      .set(el, { pointerEvents: 'none' })
      .to('.dropdown', { clipPath: 'inset(0% 0% 100% 0%)' })
  }, el)
}

watch(route, () => {
  open.value = false
  deactivate()
})

watch(isHovered, (hovering) => {
  open.value = hovering
})

watch(focused, (focused) => {
  open.value = focused

  nextTick(() => {
    if (focused)
      activate()
    else
      deactivate()
  })
})

// Enter/Space on the trigger opens the submenu (instead of navigating away)
// and moves focus to its first item, mirroring what hover already does.
async function openViaKeyboard() {
  open.value = true
  await nextTick()
  activate()
  dropdownRef.value?.querySelector<HTMLElement>('a[href], button:not([disabled])')?.focus()
}

function hasNestedChildren(item: NavigationTreeFragment) {
  return item.children?.some(children => (children?.children?.length || 0) > 0)
}
</script>

<template>
  <li class="headerNavSubmenu inline-flex" :class="open ? 'z-1' : 'z-2'">
    <ButtonNav
      :link="nav"
      class="relative z-2"
      :active="open"
      aria-haspopup="true"
      :aria-expanded="open"
      :aria-controls="submenuId"
      @keydown.enter.prevent="openViaKeyboard"
      @keydown.space.prevent="openViaKeyboard"
    />
    <Transition @enter="animateDropdownEnter" @leave="animateDropdownLeave">
      <nav
        v-show="open"
        v-if="nav.children && nav.children.length > 0"
        :id="submenuId"
        ref="dropdownRef"
        :class="{ open }"
        class="submenu left-0 right-0 top-0 fixed z-1"
      >
        <div class="dropdown container c-linen py-$header-height bg-wine">
          <ul class="layout-grid pt-10 gap-y-0">
            <template
              v-for="submenu in nav.children"
              :key="submenu!.id"
            >
              <component
                :is="hasNestedChildren(submenu as NavigationTreeFragment) ? LazySubmenuColumns : LazySubmenuSimple"
                :submenu="(submenu as NavigationTreeFragment)"
              />
            </template>
          </ul>
        </div>
      </nav>
    </Transition>
  </li>
</template>
